import { useEffect, useMemo, useState } from "react";
import { PAUSE, STATUS, fmtClock } from "../lib/labels";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";

export const StatusBadge = ({ status, testid }) => {
  const s = STATUS[status] || STATUS.finished;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest ${s.cls}`}>
      {status === "fight" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />}
      {status === "negotiation" && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
      {s.label}
    </span>
  );
};

export const Countdown = ({ war, className = "", testid }) => {
  const offset = useMemo(() => Date.parse(war.server_time) - Date.now(), [war.server_time]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!war.phase_ends_at) return <span className={className}>—</span>;
  if (war.paused) {
    return <span data-testid={testid} className={`${className} tabular-nums text-slate-400`}>{fmtClock(war.seconds_remaining || 0)}</span>;
  }
  const left = Math.max(0, Math.floor((Date.parse(war.phase_ends_at) - now - offset) / 1000));
  const color = war.status === "fight" ? "text-rose-400" : "text-amber-400";
  if (left === 0 && war.status === "fight") {
    return <span data-testid={testid} className={`${className} text-rose-300`}>A aguardar resultado</span>;
  }
  return <span data-testid={testid} className={`${className} ${color} tabular-nums`}>{fmtClock(left)}</span>;
};

export const PauseNote = ({ war, testid }) =>
  war?.paused ? (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 border border-slate-600 bg-slate-800/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-200">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />{PAUSE[war.pause_reason] || "PAUSADA"}
    </span>
  ) : null;

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Confirmar", onConfirm, testid }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="border-slate-800 bg-slate-950" data-testid={testid}>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display uppercase tracking-wider">{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-slate-400">{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`} className="border-slate-700 bg-transparent">Voltar</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-rose-600 text-white hover:bg-rose-500">
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Field = ({ label, testid, className = "", ...props }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
    <Input data-testid={testid} className="border-slate-700 bg-slate-900 font-mono" {...props} />
  </div>
);

export const FormError = ({ message, testid }) =>
  message ? (
    <div data-testid={testid} className="border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{message}</div>
  ) : null;

export const EmptyRow = ({ cols, text, testid }) => (
  <tr><td colSpan={cols} data-testid={testid} className="px-4 py-10 text-center font-mono text-sm text-slate-500">{text}</td></tr>
);

export const Stat = ({ label, value, accent = "text-slate-50", testid }) => (
  <div className="panel p-5">
    <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
    <div data-testid={testid} className={`mt-2 font-display text-3xl font-bold ${accent}`}>{value}</div>
  </div>
);
